/**
 * Ortak GET istemcisi (F1·S10). TİP KÖPRÜSÜ KURALI: her yanıt @isg/validation
 * şemasından geçer; ekran tipleri şemadan/`@isg/shared-types`ten gelir —
 * ekranda elle tip tanımı YASAK (Teknoloji v1.7). Şeması olmayan uca istek
 * atılamaz: bu fonksiyon şemasız çağrılamaz.
 */

import { API_HATA_METNI, apiHataMetni } from '@isg/validation';
import { tokenYenile } from '../auth/session';

/**
 * 401 SON ÇARESİ (işlevsel düzeltme turu — B/b): jeton süresi dolmuşsa
 * istek BİR KEZ yenilenmiş jetonla tekrar denenir.
 *
 * ⚠️ **TEK DENEME:** yenileme başarısızsa (refresh de dolmuş / IdP oturumu
 * kapanmış) ilk yanıt olduğu gibi döner ve ekranda GÖRÜNÜR hata olur —
 * sessiz sonsuz döngü YOK. Kullanıcının yazdığı veri EKRANDAN SİLİNMEZ:
 * bu katman kabuğu unmount etmez, yalnız isteği tekrarlar.
 *
 * ⚠️ **Yalnız 401.** 403 bir kabul çıktısıdır (yetki), yenilemekle
 * düzelmez — tekrar denemek yanlış olurdu.
 */
async function jetonluIstek(
  accessToken: string,
  kur: (token: string) => Promise<Response>,
): Promise<Response> {
  const ilk = await baglantiKorumali(() => kur(accessToken));
  if (ilk.status !== 401) return ilk;
  const yeni = await tokenYenile();
  if (yeni === null || yeni === accessToken) return ilk;
  return baglantiKorumali(() => kur(yeni));
}

/**
 * ✏️ KA-5 (küçük A paketi / bulgu 74) — BAĞLANTI HATASI D2 METNİ ALIR.
 * `fetch` ağ düzeyinde reddedilince (ya da yanıt gövdesi yarıda kesilince)
 * tarayıcının HAM metni (*"Load failed"* · *"Failed to fetch"*) ekrana
 * iniyordu — madde 240'ın 5xx deseni bu kolu kapsamıyordu. İptal (`AbortError`)
 * bir hata değildir, aynen geçer.
 */
async function baglantiKorumali<T>(islem: () => Promise<T>): Promise<T> {
  try {
    return await islem();
  } catch (hata) {
    if (hata instanceof DOMException && hata.name === 'AbortError') throw hata;
    if (hata instanceof ApiError) throw hata;
    if (hata instanceof TypeError) throw new ApiError(0, API_HATA_METNI.baglanti);
    throw hata;
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * ✏️ 28.09.2026 (K-T2): 4xx yanıtının HAM gövdesi. Bazı retler yalnız cümle
     * değil, kullanıcıya gösterilecek bir LİSTE de taşır (tür çıkarmada düşecek
     * içerik — 409). Ekran gövdeyi `@isg/validation` şemasından geçirerek okur
     * (tip köprüsü — §2.16); 5xx gövdesi taşınmaz (madde 240).
     */
    readonly govde: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiGet<T>(
  path: string,
  accessToken: string,
  schema: { parse(value: unknown): T },
  signal?: AbortSignal,
): Promise<T> {
  const response = await jetonluIstek(accessToken, (token) =>
    fetch(path, { headers: { Authorization: `Bearer ${token}` }, signal }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    // Madde 240 / D2: 5xx gövdesi çerçevenin HAM metnidir — ekrana hiç taşınmaz,
    // yerine `@isg/validation API_HATA_METNI` gelir; 4xx'in D2 mesajı aynen geçer.
    throw new ApiError(
      response.status,
      apiHataMetni(response.status, body?.message) ?? `API ${response.status} döndü`,
    );
  }
  return schema.parse(await baglantiKorumali(() => response.json()));
}

/**
 * Yazma istekleri (F1.5·S11 — ilk web yazma yüzeyi: davet paketi + KVKK
 * onayı). apiGet ile aynı sözleşme: yanıt şemadan geçmeden ekrana ulaşamaz.
 */
export async function apiPost<T>(
  path: string,
  accessToken: string,
  body: unknown,
  schema: { parse(value: unknown): T },
): Promise<T> {
  return apiSend('POST', path, accessToken, body, schema);
}

/** PUT (F2·S2 — tenant ayarı): apiPost ile aynı sözleşme. */
export async function apiPut<T>(
  path: string,
  accessToken: string,
  body: unknown,
  schema: { parse(value: unknown): T },
): Promise<T> {
  return apiSend('PUT', path, accessToken, body, schema);
}

/** PATCH (F2·S4 — şablon sürümü / taslak kayıt): apiPost sözleşmesi. */
export async function apiPatch<T>(
  path: string,
  accessToken: string,
  body: unknown,
  schema: { parse(value: unknown): T },
): Promise<T> {
  return apiSend('PATCH', path, accessToken, body, schema);
}

/**
 * DELETE (F2·S3 — ek silme): apiPost sözleşmesi, varsayılan gövdesiz.
 *
 * ✏️ **Tur 4½ (B1): opsiyonel gövde eklendi.** Görevlendirme *kaldırma*
 * bir SİLME DEĞİLDİR (§25/1) — kaydın kaderi gövdedeki **tarihtir**; fiil
 * HTTP fiilidir. Gövdesiz bir DELETE o tarihi taşıyamaz ve tarihi sunucuda
 * *"bugün"* diye uydurmak, geçmişe dönük kaldırmayı imkânsız kılardı.
 */
export async function apiDelete<T>(
  path: string,
  accessToken: string,
  schema: { parse(value: unknown): T },
  body?: unknown,
): Promise<T> {
  return apiSend('DELETE', path, accessToken, body, schema);
}

/**
 * Multipart yükleme (F2·S3 — aksiyon kanıt eki, Kapsam §22.4). Gövde JSON
 * DEĞİL FormData'dır (Content-Type'ı tarayıcı sınır dizesiyle kendisi
 * yazar — elle set etmek isteği bozar), ama YANIT sözleşmesi aynı: şemadan
 * geçmeden ekrana ulaşmaz.
 */
export async function apiUpload<T>(
  path: string,
  accessToken: string,
  dosya: File,
  schema: { parse(value: unknown): T },
  /** Opsiyonel düz metin form alanları (F3B·S5 — olay eki `ekTipi` gibi);
   *  doğrulama SUNUCUDADIR, buradan yalnız taşınır. */
  alanlar?: Record<string, string>,
): Promise<T> {
  const form = new FormData();
  form.append('dosya', dosya);
  for (const [ad, deger] of Object.entries(alanlar ?? {})) form.append(ad, deger);
  const response = await jetonluIstek(accessToken, (token) =>
    fetch(path, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }),
  );
  if (!response.ok) {
    const govde = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(
      response.status,
      apiHataMetni(response.status, govde?.message) ?? `API ${response.status} döndü`,
      response.status >= 500 ? null : govde,
    );
  }
  return schema.parse(await baglantiKorumali(() => response.json()));
}

/**
 * Ek indirme (F2·S3): yanıt JSON değil BAYT akışıdır — bu tek uçta şema
 * parse'ı yoktur (parse edilecek sözleşme yok), yetki + izolasyon sunucuda.
 * Blob tarayıcıya indirilir; public URL/SAS kullanılmaz (Kapsam §22.4/6).
 */
export async function apiDownload(
  path: string,
  accessToken: string,
): Promise<{ blob: Blob; dosyaAdi: string | null }> {
  const response = await jetonluIstek(accessToken, (token) =>
    fetch(path, { headers: { Authorization: `Bearer ${token}` } }),
  );
  if (!response.ok) {
    throw new ApiError(response.status, `İndirme başarısız (API ${response.status})`);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const eslesme = /filename\*=UTF-8''([^;]+)/.exec(disposition);
  return {
    blob: await baglantiKorumali(() => response.blob()),
    dosyaAdi: eslesme ? decodeURIComponent(eslesme[1]!) : null,
  };
}

async function apiSend<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  accessToken: string,
  body: unknown,
  schema: { parse(value: unknown): T },
): Promise<T> {
  const istekGovdesi = JSON.stringify(body);
  const response = await jetonluIstek(accessToken, (token) =>
    fetch(path, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: istekGovdesi,
    }),
  );
  if (!response.ok) {
    const govde = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(
      response.status,
      apiHataMetni(response.status, govde?.message) ?? `API ${response.status} döndü`,
      response.status >= 500 ? null : govde,
    );
  }
  return schema.parse(await baglantiKorumali(() => response.json()));
}
